/**
 * The peer level of the three-level editor (REQ-UI-141 / -142; ADR-039 §D-1).
 *
 * The topmost section of the detail pane: who this external system is, whether
 * Meridian loads it at all, and the peer-level kill switch. Its interfaces are
 * rendered *below* it by the dialog rather than inside it — deliberately, so
 * this form's Save cannot carry an interface edit and the two levels stay
 * separately audited (REQ-PEER-067).
 *
 * # Delete says what goes with it
 *
 * Deleting a peer cascades to its interfaces and their connections
 * (REQ-PEER-057), which is materially larger than deleting one interface. The
 * confirmation therefore names the count rather than asking a generic "are you
 * sure" — an operator who has to guess the blast radius is being asked to
 * approve something they have not been told.
 *
 * # Save is not start
 *
 * As at the interface level: start/stop is a different button on a different
 * endpoint, so it lands as its own audited event and takes effect without
 * carrying whatever else is half-typed in the form. A new peer is created
 * stopped and there is no "save and start" (REQ-PEER-061).
 *
 * Cross-references:
 *   - ADR-039 §D-1; feature-spec §3.4 / §5.1.
 *   - REQ-PEER-055 / -057 / -061 / -063; REQ-UI-141 / -142.
 */
import { useEffect, useMemo, useState } from "react";
import { DescriptionTooltip } from "@/components/ui/tooltip";
import { PeerTextInput } from "@/peers/peer-form-primitives";
import { PeerSection } from "@/peers/peer-section";
import { StatePill, StateToggleButton } from "@/peers/peer-state-controls";
import type { InterfaceState } from "@/peers/peer-catalog";
import type { PeerConfig } from "@/peers/peer-model";

/**
 * What stopping a peer does, disclosed from the state control.
 *
 * Architect-specified wording (2026-10-10), and a standing paragraph until
 * then. The provider twin (:data:`PROVIDER_STOP_HELP`) is deliberately
 * near-identical in shape but **not** in its last sentence: on ingress a stop
 * means nothing is *collected*, here it means nothing is *exchanged*. Egress
 * is bidirectional over a peer connection, so "collected" would be a false
 * description of what a stopped peer is not doing.
 */
export const PEER_STOP_HELP =
  "Stopping a peer stops all of its interfaces and their connections. Restarting a stopped " +
  "peer resumes all of its interfaces and their connections as configured. While stopped, " +
  "no data are exchanged with the peer.";

/**
 * What an export is and what it leaves out (REQ-UI-151).
 *
 * Architect-specified wording (2026-10-10). The peer bundle omits **only the
 * private-key references**, not the certificate — `peer_ca_bundle_ref` and
 * `meridian_cert_ref` do travel, because `store_cert_material` proved them
 * public when it accepted them (it rejects PEM private-key armor outright,
 * then parses X.509). That is the documented divergence from the provider
 * twin, whose Meridian certificate never passes a proving path, so the two
 * tooltips must not be made to match.
 *
 * ⚠ "imported as a new **peer**". The architect's note said "provider" here,
 * which is the provider twin's wording carried across; a peer bundle installs
 * a peer, and telling an operator otherwise on the peer surface would be
 * wrong. Raised rather than silently kept.
 */
export const PEER_EXPORT_HELP =
  "Export downloads the peer as an installable bundle recording its current configuration. " +
  "For TLS connections, private key references are excluded; these must be supplied if the " +
  "bundle is imported as a new peer.";

/** The enabled/started distinction, which the checkbox label used to carry. */
export const PEER_ENABLED_HELP =
  "Enabled controls whether the service loads this peer at all. It is distinct from " +
  "started/stopped: a disabled peer is not loaded, and a loaded one can still be stopped.";

/** Props for {@link PeerEditor}. */
export interface PeerEditorProps {
  /** The peer being edited, as a complete PUT body. */
  value: PeerConfig;
  /** True when this peer has never been persisted (drives copy + state control). */
  isNew: boolean;
  /** How many interfaces the delete would take with it; `null` while unknown. */
  interfaceCount: number | null;
  onSave: (config: PeerConfig) => void;
  onDelete: () => void;
  /**
   * Download this peer as an installable bundle (REQ-UI-151, REQ-PEER-104).
   *
   * Absent on an unsaved peer — there is nothing on the server to project.
   */
  onExport?: () => void;
  /** True while the archive is being fetched. */
  exportPending?: boolean;
  /** Operator-facing failure text, or null. */
  exportError?: string | null;
  onSetState: (state: InterfaceState) => void;
  savePending?: boolean;
  statePending?: boolean;
  /** Server-side error text, surfaced verbatim above the buttons. */
  saveError?: string | null;
  /** The other peers' names, for the local uniqueness hint (REQ-PEER-058). */
  otherNames: readonly string[];
}

/**
 * Render the peer-level form.
 *
 * ⚠ Its test ids are `peer-level-*`, not `peer-*`. The interface editor renders
 * *inside the same pane* and already owns `peer-save` / `peer-delete`, so
 * sharing them would make every selector ambiguous — and a test that grabbed the
 * wrong Save would assert a peer write against an interface route.
 */
export function PeerEditor({
  value,
  isNew,
  interfaceCount,
  onSave,
  onDelete,
  onExport,
  exportPending = false,
  exportError = null,
  onSetState,
  savePending = false,
  statePending = false,
  saveError,
  otherNames,
}: PeerEditorProps): React.ReactElement {
  const [draft, setDraft] = useState<PeerConfig>(value);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Re-seed when the selection changes, or when a save round-trips a new
  // server-side value — including a state flip, which arrives from the
  // start/stop endpoint rather than from this form.
  useEffect(() => {
    setDraft(value);
    setConfirmDelete(false);
  }, [value]);

  const patch = (next: Partial<PeerConfig>): void =>
    setDraft((current) => ({ ...current, ...next }));

  const nameError = useMemo(() => {
    const trimmed = draft.name.trim();
    if (trimmed === "") return "A name is required.";
    if (otherNames.includes(trimmed)) return "Another peer already uses this name.";
    return null;
  }, [draft.name, otherNames]);

  return (
    <form
      data-testid="peer-editor"
      onSubmit={(event) => {
        event.preventDefault();
        if (nameError === null) onSave({ ...draft, name: draft.name.trim() });
      }}
    >
      <PeerSection id="peer" title="Peer">
        <div className="flex flex-col gap-3">
          <PeerTextInput
            label="Name"
            value={draft.name}
            maxLength={128}
            onChange={(name) => patch({ name })}
            error={nameError}
            testId="peer-name"
            placeholder="north-ridge-fusion"
            hint="Unique across the deployment. Qualifies its interfaces as peer / interface."
          />
          <PeerTextInput
            label="Description"
            value={draft.description ?? ""}
            onChange={(description) => patch({ description })}
            testId="peer-description"
            hint="What this external system is, and who operates it."
          />
          <div className="flex items-center gap-1.5">
            <label className="flex items-center gap-2 text-body-sm text-text-primary">
              <input
                type="checkbox"
                checked={draft.enabled}
                data-testid="peer-enabled"
                onChange={(event) => patch({ enabled: event.target.checked })}
                className="h-3.5 w-3.5 cursor-pointer"
              />
              Enabled
            </label>
            <DescriptionTooltip
              testId="peer-enabled"
              label="Enabled"
              description={PEER_ENABLED_HELP}
            />
          </div>

          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <div className="flex items-center gap-2">
              <StatePill state={draft.state} />
              {isNew ? (
                <span className="text-meta text-text-muted" data-testid="peer-level-state-unsaved">
                  New peers are created stopped. Save first, then start it deliberately.
                </span>
              ) : (
                <StateToggleButton
                  state={draft.state}
                  pending={statePending}
                  onToggle={onSetState}
                  testId="peer-state-toggle"
                  label="peer"
                />
              )}
              {/* Beside the control it describes rather than in the section
                  header: this explains start/stop specifically, and the section
                  also holds identity, delete and export. */}
              <DescriptionTooltip
                testId="peer-level-state"
                label="stopping a peer"
                description={PEER_STOP_HELP}
              />
            </div>
          </div>

          {saveError && (
            <p
              role="alert"
              data-testid="peer-level-save-error"
              className="text-body-sm text-destructive"
            >
              {saveError}
            </p>
          )}

          <div className="flex items-center gap-2 border-t border-border pt-3">
            {!isNew &&
              (confirmDelete ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="text-meta text-destructive"
                    data-testid="peer-level-delete-warning"
                  >
                    Delete this peer
                    {interfaceCount !== null && interfaceCount > 0
                      ? ` and its ${interfaceCount} ${interfaceCount === 1 ? "interface" : "interfaces"}`
                      : ""}
                    ? This cannot be undone.
                  </span>
                  <button
                    type="button"
                    onClick={onDelete}
                    data-testid="peer-level-delete-confirm"
                    className="rounded bg-destructive px-2 py-0.5 text-meta text-destructive-foreground hover:opacity-90"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
                  >
                    Keep
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="peer-level-delete"
                  className="rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10"
                >
                  Delete peer
                </button>
              ))}
            <div className="ml-auto flex items-center gap-2">
              {/* Export sits apart from Delete deliberately: one of them takes a
                  copy OUT of the deployment and the other empties it, and a row
                  that puts them side by side invites the wrong click. */}
              {onExport !== undefined && (
                <>
                  <button
                    type="button"
                    onClick={onExport}
                    disabled={exportPending}
                    data-testid="peer-level-export"
                    className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated disabled:opacity-50"
                  >
                    {exportPending ? "Exporting…" : "Export…"}
                  </button>
                  <DescriptionTooltip
                    testId="peer-level-export"
                    label="Export"
                    description={PEER_EXPORT_HELP}
                  />
                </>
              )}
              <button
                type="submit"
                disabled={nameError !== null || savePending}
                data-testid="peer-level-save"
                className="rounded bg-brand px-3 py-1 text-body-sm font-medium text-bg-base hover:bg-brand-dim disabled:opacity-50"
              >
                {savePending ? "Saving…" : isNew ? "Create peer" : "Save peer"}
              </button>
            </div>
          </div>

          {/* REQ-UI-151's two statements — projection-not-replay, and that
              private-key references do not travel — moved into the Export
              control's own tooltip (:data:`PEER_EXPORT_HELP`) on 2026-10-10 per
              architect direction. The requirement's "renders alongside it"
              wording was amended with it; see REQ-UI-151 Verification. An
              export ERROR is not informational and stays visible. */}
          {exportError !== null && (
            <p
              role="alert"
              data-testid="peer-level-export-error"
              className="text-meta text-destructive"
            >
              {exportError}
            </p>
          )}
        </div>
      </PeerSection>
    </form>
  );
}
